import { RiCheckLine, RiFileCopyLine } from '@remixicon/react';
import { useEffect, useRef, useState } from 'react';

import { cn } from 'src/components/ui/utils';

interface ReferenceCodeChipProps {
  /** The human-quotable reference code (e.g. `smiling-otter-42`) copied on click. */
  code: string;
  className?: string;
}

/** How long the "Copied" confirmation stays visible after a successful copy. */
const COPIED_FEEDBACK_MS = 1500;

const PILL =
  'inline-flex h-[22px] items-center gap-1 self-start rounded-[6px] border border-neutral-5 bg-neutral-1 px-2 text-12 text-neutral-alpha-9 hover:bg-neutral-3';

/** Copies `text` to the clipboard via the async Clipboard API, falling back to `execCommand('copy')` on older browsers / non-secure contexts. Returns whether the copy actually succeeded. */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Clipboard API rejected (permissions / insecure context) — try the legacy path.
  }

  if (typeof document === 'undefined') return false;

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();

  let didCopy = false;
  try {
    didCopy = document.execCommand('copy');
  } catch {
    didCopy = false;
  }
  document.body.removeChild(textarea);
  return didCopy;
}

/**
 * Click-to-copy chip for an AppError reference code (TAS-3433). Renders the
 * quotable slug a user reads back to support; on click it copies the code to
 * the clipboard and briefly confirms "Copied". Purely presentational — no data
 * fetching. Stops both click AND keydown propagation so copying inside a
 * clickable cell (mouse or keyboard) never triggers the cell's own navigation.
 */
export default function ReferenceCodeChip({ code, className }: ReferenceCodeChipProps) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const didCopy = await copyToClipboard(code);
    if (!didCopy) return;
    setCopied(true);
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      // Keyboard activation (Enter/Space) fires this button's click, whose
      // stopPropagation already shields the parent. Stop the keydown too so a
      // keyboard-driven copy can never bubble and navigate the cell away.
      onKeyDown={(e) => e.stopPropagation()}
      // Stable label: the confirmation is carried ONLY by the status region
      // below, so screen readers announce "Copied" once, not twice.
      aria-label={`Copy reference code ${code}`}
      className={cn(PILL, className)}
    >
      <span className="text-neutral-alpha-8">Ref:</span>
      <span className="font-mono">{code}</span>
      {copied ? (
        <span className="inline-flex items-center gap-0.5 text-green-text">
          <RiCheckLine size={12} aria-hidden="true" />
          Copied
        </span>
      ) : (
        <RiFileCopyLine size={12} className="text-neutral-alpha-8" aria-hidden="true" />
      )}
      <span role="status" aria-live="polite" className="sr-only">
        {copied ? 'Copied' : ''}
      </span>
    </button>
  );
}
